/**
 * matchCourses.js
 *
 * Matches parsed qualification/course names from a spreadsheet against
 * existing requirements in the space and the BOHSA course API + custom_courses.
 */

import { supabase } from '@/lib/supabase';

const BOHSA_API_URL =
  'https://leqtvmuqmyxkdkaxxfqu.supabase.co/functions/v1/api/courses';
const BOHSA_API_KEY = 'bohsa-api-xDZ8kuCTQz3axI8VhhjJOQoW3YMB0jcX';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Normalise a string for comparison: lowercase, collapse whitespace, trim.
 * @param {string} str
 * @returns {string}
 */
function normalise(str) {
  return (str || '').toLowerCase().replace(/\s+/g, ' ').trim();
}

/**
 * Check whether two strings are a fuzzy (partial / contains) match.
 * Returns true when one normalised string contains the other and both are
 * at least 4 characters long (to avoid spurious short-token matches).
 *
 * @param {string} a
 * @param {string} b
 * @returns {boolean}
 */
function isFuzzyMatch(a, b) {
  const na = normalise(a);
  const nb = normalise(b);
  if (na.length < 4 || nb.length < 4) return false;
  return na.includes(nb) || nb.includes(na);
}

// ---------------------------------------------------------------------------
// searchCoursesAPI
// ---------------------------------------------------------------------------

/**
 * Search both the BOHSA external API and the custom_courses Supabase table
 * for courses matching `searchTerm`.
 *
 * Returns a combined, de-duplicated array of results in the format used by
 * AddQualification's AsyncCombobox:
 *   { value, label, group, data }
 *
 * @param {string} searchTerm
 * @param {string} companyId - UUID of the company (for custom_courses filter)
 * @returns {Promise<Array<{ value: string, label: string, group: string, data: object }>>}
 */
export async function searchCoursesAPI(searchTerm, companyId) {
  if (!searchTerm || searchTerm.trim() === '') {
    return [];
  }

  try {
    const [apiResults, dbResults] = await Promise.all([
      // --- BOHSA verified courses API ---
      fetch(`${BOHSA_API_URL}?search=${encodeURIComponent(searchTerm)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          apikey: BOHSA_API_KEY,
        },
      })
        .then((res) => res.json())
        .then((response) => {
          const courses = response?.data || [];
          if (!Array.isArray(courses)) return [];
          return courses.map((course) => ({
            value: course.name,
            label: course.name,
            group: 'Verified Courses',
            data: { ...course, _isVerifiedCourse: true },
          }));
        })
        .catch((err) => {
          console.error('BOHSA API search error:', err);
          return [];
        }),

      // --- Custom courses from Supabase ---
      supabase
        .from('custom_courses')
        .select('*')
        .ilike('title', `%${searchTerm}%`)
        .or(`company_id.eq.${companyId},company_id.is.null`)
        .limit(10)
        .then(({ data, error }) => {
          if (error) {
            console.error('custom_courses search error:', error);
            return [];
          }
          return (data || []).map((course) => ({
            value: course.title || course.name,
            label: course.title || course.name,
            group: 'Custom Courses',
            data: course,
          }));
        }),
    ]);

    // Combine and de-duplicate by value (title)
    const combined = [...apiResults, ...dbResults];
    const seen = new Set();
    const unique = [];
    for (const item of combined) {
      if (!seen.has(item.value)) {
        seen.add(item.value);
        unique.push(item);
      }
    }
    return unique;
  } catch (error) {
    console.error('searchCoursesAPI error:', error);
    return [];
  }
}

// ---------------------------------------------------------------------------
// batchSearchCourses
// ---------------------------------------------------------------------------

/**
 * Search courses for multiple qualification names with rate-limiting.
 *
 * Processes names in groups of 5, with a 200 ms delay between groups.
 * Calls `onProgress(completed, total)` after each individual search completes.
 *
 * @param {string[]} qualNames - Array of qualification name strings to search
 * @param {string} companyId - UUID of the company
 * @param {(completed: number, total: number) => void} [onProgress] - Progress callback
 * @returns {Promise<Map<string, Array<{ value: string, label: string, group: string, data: object }>>>}
 */
export async function batchSearchCourses(qualNames, companyId, onProgress) {
  const results = new Map();
  const total = qualNames.length;
  let completed = 0;

  const GROUP_SIZE = 5;
  const DELAY_MS = 200;

  for (let i = 0; i < total; i += GROUP_SIZE) {
    const group = qualNames.slice(i, i + GROUP_SIZE);

    // Process each group in parallel
    await Promise.all(
      group.map(async (name) => {
        const searchResults = await searchCoursesAPI(name, companyId);
        results.set(name, searchResults);
        completed++;
        if (onProgress) {
          onProgress(completed, total);
        }
      })
    );

    // Rate-limit delay between groups (skip after the last group)
    if (i + GROUP_SIZE < total) {
      await new Promise((resolve) => setTimeout(resolve, DELAY_MS));
    }
  }

  return results;
}

// ---------------------------------------------------------------------------
// matchCourses
// ---------------------------------------------------------------------------

/**
 * Match parsed qualification names from a spreadsheet against existing
 * requirements in the space and (for unmatched names) against the BOHSA
 * course API + custom_courses table.
 *
 * @param {Array<{ rawName: string, colIndex: number }>} parsedQualifications
 *   Qualification headers extracted from the spreadsheet.
 * @param {Array<{ id: string, name: string, course_link_id: string|null, course_link_type: string|null }>} existingRequirements
 *   Existing compliance_requirements for the current space.
 * @param {string} companyId - UUID of the company.
 * @returns {Promise<Array<{
 *   rawName: string,
 *   colIndex: number,
 *   matchType: 'existing_exact' | 'existing_fuzzy' | 'api_match' | 'none',
 *   matchedRequirement: object | null,
 *   searchResults: Array<{ value: string, label: string, group: string, data: object }>,
 *   action: 'use_existing' | 'create_from_course' | 'create_custom' | 'skip'
 * }>>}
 */
export async function matchCourses(
  parsedQualifications,
  existingRequirements,
  companyId,
  onProgress
) {
  const results = [];
  const total = parsedQualifications.length;
  let completed = 0;

  // Build a lookup of normalised existing requirement names for fast matching
  const normalisedExisting = existingRequirements.map((req) => ({
    ...req,
    _normalised: normalise(req.name),
  }));

  // Names that need an API search (not matched locally)
  const unmatchedEntries = [];

  // --- Pass 1: local matching (exact then fuzzy) ---
  for (const qual of parsedQualifications) {
    const normName = normalise(qual.rawName);

    // 1a. Exact match (case-insensitive)
    const exactMatch = normalisedExisting.find(
      (req) => req._normalised === normName
    );
    if (exactMatch) {
      results.push({
        rawName: qual.rawName,
        colIndex: qual.colIndex,
        matchType: 'existing_exact',
        matchedRequirement: exactMatch,
        searchResults: [],
        action: 'use_existing',
      });
      completed++;
      if (onProgress) onProgress(completed, total);
      continue;
    }

    // 1b. Fuzzy / partial match
    const fuzzyMatch = normalisedExisting.find((req) =>
      isFuzzyMatch(req._normalised, normName)
    );
    if (fuzzyMatch) {
      results.push({
        rawName: qual.rawName,
        colIndex: qual.colIndex,
        matchType: 'existing_fuzzy',
        matchedRequirement: fuzzyMatch,
        searchResults: [],
        action: 'use_existing',
      });
      completed++;
      if (onProgress) onProgress(completed, total);
      continue;
    }

    // Not matched locally — queue for API search
    unmatchedEntries.push(qual);
  }

  // --- Pass 2: API search for unmatched qualifications ---
  if (unmatchedEntries.length > 0) {
    // Wrap progress so it adds onto the locally-matched count
    const locallyMatched = completed;
    const searchMap = await batchSearchCourses(
      unmatchedEntries.map((e) => e.rawName),
      companyId,
      (batchDone) => {
        if (onProgress) onProgress(locallyMatched + batchDone, total);
      }
    );

    for (const qual of unmatchedEntries) {
      const searchResults = searchMap.get(qual.rawName) || [];

      if (searchResults.length > 0) {
        results.push({
          rawName: qual.rawName,
          colIndex: qual.colIndex,
          matchType: 'api_match',
          matchedRequirement: null,
          searchResults,
          action: 'create_from_course',
        });
      } else {
        results.push({
          rawName: qual.rawName,
          colIndex: qual.colIndex,
          matchType: 'none',
          matchedRequirement: null,
          searchResults: [],
          action: 'create_custom',
        });
      }
    }
  }

  return results;
}
